import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Sparkle, MapPin, Star, BookmarkSimple, Storefront, ArrowRight, Heart } from "@phosphor-icons/react";
import ImgFallback from "@/components/ImgFallback";
import { toast } from "sonner";

// A small curated Inspiration band — wider editorial content that is
// intentionally NOT bookable. It exists to inspire, not to sell.
const INSPIRATION = [
  {
    id: "insp-1",
    image: "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?w=1200&q=85&auto=format&fit=crop",
    caption: "Long hair, softened waves.",
    tag: "Inspiration",
  },
  {
    id: "insp-2",
    image: "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=1200&q=85&auto=format&fit=crop",
    caption: "Skin fade, editorial.",
    tag: "Inspiration",
  },
  {
    id: "insp-3",
    image: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=1200&q=85&auto=format&fit=crop",
    caption: "Textured layers.",
    tag: "Inspiration",
  },
];

function TonightCard({ s }) {
  return (
    <Link
      to={`/studio/${s.id}`}
      className="min-w-[260px] md:min-w-[300px] group snap-start"
      data-testid={`studio-card-${s.id}`}
    >
      <div className="relative overflow-hidden rounded-sm">
        <ImgFallback
          src={s.hero_look || s.portfolio?.[0] || s.cover_image}
          alt={`${s.name} — haircut result`}
          className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="chip bg-gold text-ink font-semibold border-transparent">Tonight · {s.tonight_slot || "9:00pm"}</span>
        </div>
        <div className="absolute top-3 right-3 chip bg-ink/70 backdrop-blur">
          <Star size={12} weight="fill" color="#C5A059" /> {s.rating}
        </div>
      </div>
      <div className="mt-3">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="font-display text-lg tracking-tight text-cream truncate">{s.name}</h4>
          <span className="text-xs text-gold whitespace-nowrap">from ${s.starting_price}</span>
        </div>
        <p className="text-xs text-cream/60 mt-0.5 truncate">{s.professional_name} · {s.area} · {s.distance_km}km</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40 mt-1">{s.completed_cuts}+ verified cuts</p>
      </div>
    </Link>
  );
}

function FeedTile({ f, big }) {
  const [saved, setSaved] = useState(false);
  const [following, setFollowing] = useState(false);
  const navigate = useNavigate();
  const onSave = (e) => {
    e.preventDefault(); e.stopPropagation();
    setSaved(!saved);
    toast.success(saved ? "Look removed" : "Look saved");
  };
  const onFollow = (e) => {
    e.preventDefault(); e.stopPropagation();
    setFollowing(!following);
    toast.success(following ? "Unfollowed Studio" : "Following Studio");
  };
  const goStudio = (e) => {
    e.preventDefault(); e.stopPropagation();
    navigate(`/studio/${f.studio_id}`);
  };
  return (
    <Link
      to={`/look/${f.id}`}
      data-testid={`feed-item-${f.id}`}
      className={`relative overflow-hidden rounded-sm group ${big ? "col-span-2 row-span-2 aspect-square" : "aspect-[3/4]"}`}
    >
      <ImgFallback
        src={f.image}
        alt={f.studio_name ? `${f.studio_name} — look` : "look"}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
      <div className="absolute top-3 left-3 flex gap-2">
        <span className="chip bg-ink/70 backdrop-blur text-[10px] py-1 px-2">Bookable · {f.area}</span>
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-cream font-medium truncate">{f.studio_name}</p>
          {f.service && <p className="text-[10px] uppercase tracking-[0.2em] text-cream/60">{f.service} · ${f.price}</p>}
        </div>
      </div>
      {/* Progressive-disclosure action bar — hover on desktop */}
      <div className="hidden md:flex absolute inset-x-3 bottom-3 items-center justify-end gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        <button onClick={onSave} data-testid={`feed-save-${f.id}`} className="w-9 h-9 rounded-full bg-ink/80 backdrop-blur border border-cream/15 hover:border-gold flex items-center justify-center" aria-label="Save look">
          <BookmarkSimple size={16} weight={saved ? "fill" : "regular"} color={saved ? "#C5A059" : "#FAF9F6"} />
        </button>
        <button type="button" onClick={onFollow} data-testid={`feed-follow-${f.id}`} className="w-9 h-9 rounded-full bg-ink/80 backdrop-blur border border-cream/15 hover:border-gold flex items-center justify-center" aria-label="Follow Studio">
          <Heart size={16} weight={following ? "fill" : "regular"} color={following ? "#C5A059" : "#FAF9F6"} />
        </button>
        <button
          type="button"
          onClick={goStudio}
          data-testid={`feed-view-studio-${f.id}`}
          className="w-9 h-9 rounded-full bg-ink/80 backdrop-blur border border-cream/15 hover:border-gold flex items-center justify-center"
          aria-label="View Studio"
        >
          <Storefront size={16} color="#FAF9F6" />
        </button>
        <span className="btn-gold px-3 h-9 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1">
          Book this look <ArrowRight size={12} weight="bold" />
        </span>
      </div>
      <button
        onClick={onSave}
        data-testid={`feed-save-m-${f.id}`}
        className="md:hidden absolute top-3 right-3 w-9 h-9 rounded-full bg-ink/70 backdrop-blur border border-cream/15 flex items-center justify-center"
        aria-label="Save look"
      >
        <BookmarkSimple size={16} weight={saved ? "fill" : "regular"} color={saved ? "#C5A059" : "#FAF9F6"} />
      </button>
    </Link>
  );
}

export default function Discover() {
  const [data, setData] = useState({ studios: [], feed: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/discover").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-5 py-10 text-cream/50" data-testid="discover-loading">Loading Discover…</div>;
  }

  const studios = data.studios;
  // Build feed items with local info (bookable) from studios
  const localFeed = studios.flatMap((s) =>
    (s.portfolio || []).slice(0, 3).map((img, i) => ({
      id: `${s.id}-${i}`,
      studio_id: s.id,
      studio_name: s.name,
      professional_name: s.professional_name,
      image: img,
      city: s.city,
      area: s.area,
      rating: s.rating,
      service: s.services?.[0]?.name,
      price: s.services?.[0]?.price,
    }))
  );

  const heroStudio = studios[0]; // Steve's Studio — seed order
  const heroImage = heroStudio?.hero_look || heroStudio?.portfolio?.[0];
  const rail = studios.slice(0, 8);
  const gridFeed = localFeed.slice(1, 13);

  return (
    <div className="max-w-6xl mx-auto pt-6 pb-24" data-testid="discover-page">
      {/* Editorial header */}
      <div className="px-5 flex items-end justify-between mb-8">
        <div>
          <p className="overline mb-2">Discover</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-cream">
            Good hair isn't luck,<br /><span className="text-gold">it's KUTZ.</span>
          </h1>
        </div>
      </div>

      {/* Hero — hair is the subject */}
      {heroStudio && (
        <Link
          to={`/studio/${heroStudio.id}`}
          className="block relative overflow-hidden md:rounded-sm mx-0 md:mx-5 mb-6 group"
          data-testid="hero-look"
        >
          <ImgFallback
            src={heroImage}
            alt={`${heroStudio.name} — signature look`}
            className="w-full h-[68vh] md:h-[72vh] object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
          <div className="absolute top-4 left-4 md:top-6 md:left-6 flex gap-2">
            <span className="chip bg-gold text-ink font-semibold border-transparent">Editor's Look · Bookable</span>
          </div>
          <div className="absolute bottom-5 left-5 right-5 md:bottom-8 md:left-8 md:right-8 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="overline mb-2">Signature Cut · Tonight {heroStudio.tonight_slot}</p>
              <h2 className="font-display text-3xl md:text-5xl text-cream tracking-tighter truncate">{heroStudio.name}</h2>
              <p className="text-sm text-cream/80 mt-1 flex items-center gap-1">
                <MapPin size={14} weight="fill" color="#C5A059" /> {heroStudio.area}, {heroStudio.city}
                <span className="divider-dot" />
                <Star size={12} weight="fill" color="#C5A059" /> {heroStudio.rating}
              </p>
            </div>
            <span className="btn-gold hidden md:inline-block px-5 py-3 rounded-full text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap">Book this look</span>
          </div>
        </Link>
      )}

      {/* Available Tonight rail */}
      <section className="mt-10 md:mt-14 px-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" color="#C5A059" />
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">Available tonight</h3>
          </div>
          <Link to="/explore" className="text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-gold" data-testid="link-see-all-tonight">See all</Link>
        </div>
        <p className="text-xs text-cream/50 mb-4">Real slots near you. Book direct.</p>
      </section>
      <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory px-5 pb-2">
        {rail.map((s) => <TonightCard key={s.id} s={s} />)}
      </div>

      {/* Local Legends header — feed of local, bookable looks */}
      <section className="mt-14 px-5">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="overline mb-1">Local Legends</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">Bookable looks near you</h3>
          </div>
        </div>
      </section>
      <div className="px-2 md:px-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {gridFeed.map((f, i) => (
            <FeedTile key={f.id} f={f} big={i % 5 === 0} />
          ))}
        </div>
      </div>

      {/* Wider inspiration — clearly labelled, NOT bookable */}
      <section className="mt-16 px-5">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="overline mb-1">Inspiration</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">Wider editorial</h3>
            <p className="text-xs text-cream/50 mt-1">Aspirational looks. Not tied to a local Studio.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {INSPIRATION.map((i) => (
            <div key={i.id} className="relative aspect-[4/5] overflow-hidden rounded-sm group" data-testid={`inspiration-${i.id}`}>
              <ImgFallback src={i.image} alt={i.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
              <div className="absolute top-3 left-3 chip bg-ink/70 backdrop-blur text-[10px]">Inspiration</div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-sm text-cream/90">{i.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 mx-5 border-t border-cream/10 pt-10">
        <p className="overline mb-3">Our promise</p>
        <p className="font-display text-xl md:text-2xl text-cream leading-relaxed max-w-2xl">
          KUTZ isn't here to decide who succeeds.<br />We're here to make sure talent has the opportunity to be seen.
        </p>
      </section>
    </div>
  );
}
